import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import { COMMON_PROTOCOL } from '@constants';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import UpdateDatabaseForm from '@modules/digibesShop/restaurant/UpdateDatabaseForm';
import { showErrorMessage } from '@services/notifyService';
import { parseConnectionString } from '@utils';
import { Button, Flex, Tabs } from 'antd';

import RestaurantForm from './RestaurantForm';

const message = defineMessages({
    titleUpdate: 'Update Restaurant',
    titleCreate: 'Create Restaurant',
    titleUpdateDb: 'Update DB Config',
    titleCreateDb: 'Create DB Config',
    updateSuccess: 'Update restaurant successfully',
    updateDbSuccess: 'Update DB config successfully',
    createDbSuccess: 'Create DB config successfully',
    getError: 'Get DB config error',
    restaurantTab: 'Restaurant',
    databaseTab: 'Database',
});

const RESTAURANT_FORM_ID = 'form-restaurant-tab';
const DB_FORM_ID = 'form-database-tab';

const useRestaurantFormModal = ({ onReloadList } = {}) => {
    const translate = useTranslate();
    const notification = useNotification();

    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('restaurant');
    const [restaurantDetail, setRestaurantDetail] = useState(null);
    const [dbDetail, setDbDetail] = useState(null);
    const [restaurantId, setRestaurantId] = useState(null);
    const [isDbEditing, setIsDbEditing] = useState(false);
    const [restaurantChanged, setRestaurantChanged] = useState(false);
    const [dbChanged, setDbChanged] = useState(false);

    // Restaurant fetch/save
    const { execute: executeGetRestaurant } = useFetch(apiConfig.restaurant.getById, { immediate: false });
    const { execute: executeSaveRestaurant, loading: loadingSaveRestaurant } = useFetch(apiConfig.restaurant.update, {
        immediate: false,
    });

    // Database fetch/save
    const { execute: executeGetDbInfo } = useFetch(apiConfig.user.getShopDatabaseInfo, { immediate: false });
    const { execute: executeCreateDb, loading: loadingCreateDb } = useFetch(apiConfig.user.createShopDatabaseInfo, {
        immediate: false,
    });
    const { execute: executeUpdateDb, loading: loadingUpdateDb } = useFetch(apiConfig.user.updateShopDatabaseInfo, {
        immediate: false,
    });

    const handleSaveError = (err) => {
        const errorCode = err?.response?.data?.code ?? err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) showErrorMessage(translate.formatMessage(errorMessage), translate);
        else showErrorMessage(err?.message || translate.formatMessage(commonMessage.error), translate);
    };

    const closeModal = () => {
        setOpen(false);
        setRestaurantDetail(null);
        setDbDetail(null);
        setRestaurantId(null);
        setIsDbEditing(false);
        setRestaurantChanged(false);
        setDbChanged(false);
        setActiveTab('restaurant');
    };

    const fetchRestaurantDetail = (id) => {
        executeGetRestaurant({
            pathParams: { id },
            onCompleted: (res) => {
                setRestaurantDetail(res?.data);
            },
            onError: () => {
                setRestaurantDetail({});
            },
        });
    };

    const fetchDbInfo = (id) => {
        executeGetDbInfo({
            pathParams: { id },
            onCompleted: (res) => {
                const raw = res?.data;
                const parsed = raw ? { ...raw, ...parseConnectionString(raw.url) } : {};
                setDbDetail(parsed);
                setIsDbEditing(Object.keys(parsed).length > 0);
            },
            onError: (err) => {
                if (err?.code === 'ERROR-DB-CONFIG-001') {
                    setDbDetail({});
                    setIsDbEditing(false);
                } else {
                    notification({ type: 'error', message: translate.formatMessage(message.getError) });
                }
            },
        });
    };

    // Open modal for restaurant edit (default tab: restaurant)
    const openRestaurantModal = (record) => {
        const id = record.id;
        setRestaurantId(id);
        setActiveTab('restaurant');
        setRestaurantChanged(false);
        setDbChanged(false);
        fetchRestaurantDetail(id);
        fetchDbInfo(id);
        setOpen(true);
    };

    // Open modal for database edit (default tab: database)
    const openDatabaseModal = (record) => {
        const id = record.id;
        setRestaurantId(id);
        setActiveTab('database');
        setRestaurantChanged(false);
        setDbChanged(false);
        fetchRestaurantDetail(id);
        fetchDbInfo(id);
        setOpen(true);
    };

    // Save restaurant handler
    const handleSaveRestaurant = (values) => {
        const preparedData = {
            ...values,
            id: restaurantDetail?.id,
            status: values.status || restaurantDetail?.status,
            settings: typeof values.settings === 'string' ? values.settings : JSON.stringify(values.settings),
        };
        executeSaveRestaurant({
            data: preparedData,
            onCompleted: (res) => {
                if (res?.result === true) {
                    notification({ message: translate.formatMessage(message.updateSuccess) });
                    closeModal();
                    onReloadList?.();
                }
            },
            onError: handleSaveError,
        });
    };

    // Save database handler
    const buildJdbcPayload = (values) => {
        const { host, port, database, username, password, driverClassName, initialize, maxConnection } = values;
        const url = `${COMMON_PROTOCOL}${host}:${port}/${database}?useUnicode=yes&characterEncoding=UTF-8&zeroDateTimeBehavior=convertToNull`;
        return {
            url,
            username,
            password,
            driverClassName,
            initialize: Number(initialize),
            maxConnection: parseInt(maxConnection),
        };
    };

    const handleSaveDatabase = (values) => {
        const payload = isDbEditing
            ? { ...buildJdbcPayload(values), id: dbDetail?.id }
            : { ...buildJdbcPayload(values), restaurantId, serverProviderId: values.serverProviderId };

        const execute = isDbEditing ? executeUpdateDb : executeCreateDb;
        const successMessage = isDbEditing ? message.updateDbSuccess : message.createDbSuccess;

        execute({
            data: payload,
            onCompleted: (res) => {
                if (res?.result === true) {
                    notification({ message: translate.formatMessage(successMessage) });
                    closeModal();
                    onReloadList?.();
                }
            },
            onError: handleSaveError,
        });
    };

    const isSaveDisabled = activeTab === 'restaurant' ? !restaurantChanged : !dbChanged;
    const isLoading = activeTab === 'restaurant' ? loadingSaveRestaurant : loadingCreateDb || loadingUpdateDb;

    const titleModal = activeTab === 'restaurant'
        ? translate.formatMessage(restaurantDetail?.id ? message.titleUpdate : message.titleCreate)
        : translate.formatMessage(isDbEditing ? message.titleUpdateDb : message.titleCreateDb);

    const renderModal = () =>
        open && (
            <BaseModal
                isModalVisible={open}
                handleCancel={closeModal}
                title={titleModal}
                width="900px"
                noBodyPadding={true}
                customFooter={
                    <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                        <Button danger onClick={closeModal} icon={<StopOutlined />}>
                            {translate.formatMessage(commonMessage.cancel)}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            form={activeTab === 'restaurant' ? RESTAURANT_FORM_ID : DB_FORM_ID}
                            loading={isLoading}
                            disabled={isSaveDisabled}
                            icon={<SaveOutlined />}
                        >
                            {translate.formatMessage(commonMessage.update)}
                        </Button>
                    </Flex>
                }
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    style={{ padding: '0 16px' }}
                    items={[
                        {
                            key: 'restaurant',
                            label: translate.formatMessage(message.restaurantTab),
                            children: (
                                <RestaurantForm
                                    formId={RESTAURANT_FORM_ID}
                                    dataDetail={restaurantDetail || {}}
                                    isEditing={true}
                                    actions={null}
                                    onSubmit={handleSaveRestaurant}
                                    setIsChangedFormValues={setRestaurantChanged}
                                    cardProps={{ styles: { body: { padding: 8 } } }}
                                />
                            ),
                        },
                        {
                            key: 'database',
                            label: translate.formatMessage(message.databaseTab),
                            children: (
                                <UpdateDatabaseForm
                                    formId={DB_FORM_ID}
                                    dataDetail={dbDetail || {}}
                                    isEditing={isDbEditing}
                                    actions={null}
                                    onSubmit={handleSaveDatabase}
                                    setIsChangedFormValues={setDbChanged}
                                    restaurantId={restaurantId}
                                    cardProps={{ styles: { body: { padding: 8 } } }}
                                />
                            ),
                        },
                    ]}
                />
            </BaseModal>
        );

    return {
        openRestaurantModal,
        openDatabaseModal,
        closeModal,
        renderModal,
        setActiveTab,
    };
};

export default useRestaurantFormModal;